import { Box, Theme, SxProps } from '@mui/material';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsAxis } from '@mui/x-charts/ChartsAxis';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import Typography from '../Typography';
import { ArrowDropDownIcon } from '../Icons';

export type HistogramRow<AdditionalProps = object> = AdditionalProps & {
    /** the bin label that will be shown */
    label: string | undefined;
    /** the bin range (min - max) */
    range: string | undefined;
    /** the count/frequency of this category/bin, will be displayed on x axis */
    value: number | null;
    /** optional color per category, accepts any valid css color string (hex, rgb(a), named colors, etc) */
    color?: string | null;
};

export type HistogramData = {
    bins: HistogramRow[];
    /** total number of pixels in COG overview returned by TiTiler  */
    totalCount: number;
    /** count of excluded pixels from the chart. this includes values below the noise threshold for MSAT */
    excludedCount: number;
};

/**
 * If a color isn't included in the data rows, chartColor is used instead. This can also be used for single color charts.
 * tooltipLabelHandler will customize the way the value displays when the bar is hovered over if provided, or display the value if not.
 * barLabelHandler will customize the text that displays on the bar itself if provided.
 */
export interface HistogramProps {
    data: HistogramData;
    title?: string;
    legend?: ReactElement;
    chartColor: string;
    xAxisLabel?: string;
    yAxisLabel?: string;
    yAxisWidth?: number;
    tooltipLabelHandler?: (value: number | null, context: { dataIndex: number }, data: HistogramRow[]) => string | null;
    tooltipBinFormatter?: (value: string, location: string) => string;
    barLabelHandler?: (
        item: { value: number | null; dataIndex: number },
        data: HistogramRow[]
    ) => string | null | undefined;
    caption?: ReactElement;
    selectedValue?: number;
    sx?: SxProps<Theme>;
}

export const getHighlightedDataIndex = (bins: HistogramRow[], selectedValue: number | undefined) => {
    if (selectedValue === undefined) return -1;

    return bins.findIndex((bin) => {
        const [minValue, maxValue] = (bin.range ?? '').split('-').map((value) => Number.parseFloat(value.trim()));
        return selectedValue >= minValue && selectedValue < maxValue;
    });
};

export const Histogram = ({
    data,
    title,
    legend,
    chartColor,
    yAxisWidth,
    yAxisLabel,
    xAxisLabel,
    tooltipLabelHandler,
    tooltipBinFormatter,
    barLabelHandler,
    caption,
    selectedValue,
    sx
}: HistogramProps) => {
    const { bins, totalCount } = data;
    const chartWrapperRef = useRef<HTMLDivElement>(null);
    const [iconLeft, setIconLeft] = useState<number | null>(null);

    const highlightedDataIndex = getHighlightedDataIndex(bins, selectedValue);
    const namesArray = bins.map((item) => item.label);
    const percentagesArray = bins.map((item) => {
        const value = item.value ?? 0;
        return totalCount > 0 ? (value / totalCount) * 100 : 0;
    });
    const colorsArray = bins.map((item) => (item.color ? item.color : chartColor));

    //allows for custom tooltip label formatting by passing in the value and context (context.dataIndex is particularly useful) and
    //the full histogram data array
    const seriesValueFormatter = (value: number | null, context: { dataIndex: number }) => {
        return tooltipLabelHandler ? tooltipLabelHandler(value, context, bins) : '';
    };

    //allows for custom label formatting on the bar itself by passing in the item (item.dataIndex is particularly useful) and
    //the full histogram data array
    const barLabelFormatter = (item: { value: number | null; dataIndex: number }) => {
        return barLabelHandler ? barLabelHandler(item, bins) : '';
    };

    // Set/update the position for the icon over the bar that holds the selected value
    useEffect(() => {
        const updateIconPosition = () => {
            if (!chartWrapperRef.current || highlightedDataIndex < 0) {
                setIconLeft(null);
                return;
            }

            const selectedBar = chartWrapperRef.current.querySelector(`rect.histogram-bar-${highlightedDataIndex}`);
            if (!selectedBar) {
                setIconLeft(null);
                return;
            }

            const wrapperRect = chartWrapperRef.current.getBoundingClientRect();
            const barRect = selectedBar.getBoundingClientRect();
            const iconLeft = barRect.left - wrapperRect.left + barRect.width / 2;

            setIconLeft(iconLeft);
        };

        const frame = requestAnimationFrame(updateIconPosition);
        const resizeObserver = new ResizeObserver(updateIconPosition);

        if (chartWrapperRef.current) {
            resizeObserver.observe(chartWrapperRef.current);
        }
        window.addEventListener('resize', updateIconPosition);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateIconPosition);
        };
    }, [highlightedDataIndex, bins.length]);

    return (
        <>
            <Typography variant="h6" align="center">
                {title}
            </Typography>

            {legend}

            <Box ref={chartWrapperRef} sx={{ position: 'relative', width: 'fit-content' }}>
                <ChartContainer
                    sx={[...(Array.isArray(sx) ? sx : [sx])]}
                    yAxis={[{ label: xAxisLabel, min: 0, width: yAxisWidth }]}
                    xAxis={[
                        {
                            id: 'barCategories',
                            scaleType: 'band',
                            label: yAxisLabel,
                            data: namesArray,
                            categoryGapRatio: 0,
                            colorMap: { type: 'ordinal', colors: colorsArray },
                            hideTooltip: false,
                            tickLabelPlacement: 'tick',
                            // If bins.length is even, only label odd indices and vice versa
                            tickLabelInterval: (_value, index) => bins.length % 2 !== index % 2,
                            valueFormatter(value, context) {
                                return tooltipBinFormatter ? tooltipBinFormatter(value, context.location) : '';
                            }
                        }
                    ]}
                    series={[
                        {
                            data: percentagesArray,
                            valueFormatter: seriesValueFormatter,
                            type: 'bar',
                            layout: 'vertical',
                            highlightScope: { highlight: 'none' }
                        }
                    ]}
                >
                    <BarPlot
                        barLabel={barLabelFormatter}
                        slotProps={{
                            bar: ({ dataIndex }) => {
                                return {
                                    className: `histogram-bar-${dataIndex}`
                                };
                            }
                        }}
                    />
                    <ChartsAxis />
                    <ChartsAxisHighlight x="band" />
                    <ChartsTooltip />
                </ChartContainer>

                {/* icon over the bar with the selection */}
                {iconLeft !== null && (
                    <ArrowDropDownIcon
                        sx={{
                            position: 'absolute',
                            left: iconLeft,
                            top: 2,
                            transform: 'translateX(-50%)',
                            fontSize: 26
                        }}
                        data-testid="histogram-selected-bar-arrow"
                    />
                )}
            </Box>
            {caption}
        </>
    );
};
